/* === solara-form.jsx === */
// Solara Living shared form runtime.
//
// Provides three things to every page that has a lead form:
//   - WizardForm        : the multi-step wizard used by the home page + 4 product pages
//   - FileUploadField   : <input type=file> with chips, validation, and visible state
//   - CommentUploadsStep: the final "anything we should see?" step that replaces
//                         the legacy time-slot picker (per session 1b spec)
//   - submitLead        : multipart POST helper that targets /api/submit.php
//   - useFormSession    : session_id + debounced /api/track.php tracking
//
// Loaded BEFORE any page-specific jsx so the page jsx can render <WizardForm/>
// without redefining wizard mechanics in five places.
//
// File limits are enforced both client- and server-side. The server source of
// truth is api/config.example.php (uploads.max_bytes / .max_files). Keep these
// in sync when bumping caps.

// --- CMS content lookup helpers (top-level so every page that loads this file
// can access them via bare `_C` / `_CA`). On the home page they're shadowed
// (redeclared) by the copies in solara-sections.jsx — harmless, second one
// wins. about-page.jsx defines its own copies because it does not load this
// file.
function _C(path, fallback){
  const root = (typeof window !== "undefined" && window.__SOLARA_CONTENT__) || (typeof globalThis !== "undefined" && globalThis.__SOLARA_CONTENT__) || null;
  if (!root) return fallback;
  const v = String(path).split('.').reduce((o, k) => (o == null ? o : o[k]), root);
  return (v === undefined || v === null || v === '') ? fallback : v;
}
function _CA(path, fallback){
  const v = _C(path, null);
  return Array.isArray(v) && v.length ? v : fallback;
}

(() => {
  const { useState, useEffect, useRef, useMemo, useCallback } = React;

  // --- Inject file-upload CSS once -------------------------------------------
  // The form-card/.field/.opts styles already live inline in every page HTML.
  // We only need to add the new classes introduced by FileUploadField, so we
  // inject a <style> tag the first time solara-form.jsx runs.
  if (typeof document !== 'undefined' && !document.getElementById('solara-form-css')) {
    const style = document.createElement('style');
    style.id = 'solara-form-css';
    style.textContent = `
      .field-files .files-help {
        font-size: 13px; color: var(--muted, #6b6258); margin: 4px 0 10px;
      }
      .field-files .files-list {
        display: flex; flex-wrap: wrap; gap: 8px; align-items: stretch;
      }
      .field-files .file-chip {
        display: inline-flex; align-items: center; gap: 8px;
        padding: 8px 10px 8px 14px;
        background: var(--bg-2, #efe7da); border-radius: 999px;
        font-size: 14px; max-width: 100%; color: var(--text, #2b2620);
      }
      .field-files .file-chip-name {
        max-width: 220px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .field-files .file-chip-size {
        font-size: 12px; color: var(--muted, #6b6258);
      }
      .field-files .file-chip-remove {
        appearance: none; border: 0; background: transparent;
        width: 22px; height: 22px; border-radius: 50%;
        font-size: 17px; line-height: 1; color: var(--muted, #6b6258);
        cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
        transition: background 120ms ease, color 120ms ease;
      }
      .field-files .file-chip-remove:hover {
        background: rgba(0,0,0,0.08); color: var(--text, #2b2620);
      }
      .field-files .file-add {
        appearance: none;
        display: inline-flex; align-items: center; gap: 8px;
        padding: 9px 14px;
        background: transparent;
        border: 1.5px dashed var(--border, #c9bda7);
        border-radius: 999px;
        font-size: 14px; color: var(--text, #2b2620);
        cursor: pointer;
        transition: border-color 120ms ease, background 120ms ease;
      }
      .field-files .file-add:hover {
        border-color: var(--text, #2b2620);
        background: rgba(0,0,0,0.03);
      }
      .field-files .file-add-plus {
        font-size: 16px; line-height: 1; color: var(--muted, #6b6258);
      }
    `;
    document.head.appendChild(style);
  }

  // --- Constants (mirror api/config.example.php) -----------------------------
  const FORM_MAX_BYTES = 8 * 1024 * 1024; // 8 MB per file
  const FORM_MAX_FILES = 3;
  const FORM_ALLOWED_EXT = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'pdf'];
  const FORM_ALLOWED_ACCEPT = '.jpg,.jpeg,.png,.webp,.heic,.heif,.pdf,image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf';

  // --- uuid v4 (no crypto.randomUUID for old Safari) -------------------------
  function uuidV4() {
    const bytes = new Uint8Array(16);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
  }

  function sessionKey(formId) {
    return `solara_form_session_${formId || 'main'}`;
  }

  function loadSession(formId) {
    try {
      const v = localStorage.getItem(sessionKey(formId));
      if (v && /^[0-9a-f-]{36}$/i.test(v)) return v;
    } catch (e) { /* localStorage unavailable */ }
    const fresh = uuidV4();
    try { localStorage.setItem(sessionKey(formId), fresh); } catch (e) {}
    return fresh;
  }

  function clearSession(formId) {
    try { localStorage.removeItem(sessionKey(formId)); } catch (e) {}
  }

  // --- UTM / referrer capture (one-time, persisted) --------------------------
  // Parsed once per page load. Used as request fields on every track + submit
  // call so the lead row gets first-touch attribution.
  function readAttribution() {
    const url = new URL(location.href);
    const q = url.searchParams;
    return {
      page_url:     location.href,
      referrer:     document.referrer || '',
      utm_source:   q.get('utm_source')   || '',
      utm_medium:   q.get('utm_medium')   || '',
      utm_campaign: q.get('utm_campaign') || '',
      utm_content:  q.get('utm_content')  || '',
      utm_term:     q.get('utm_term')     || '',
    };
  }

  // --- useFormSession --------------------------------------------------------
  // Returns { sessionId, track, formStartedAt, secondsOnForm } for a given
  // formId. `track` is debounced ~600ms so typing doesn't flood the endpoint;
  // step transitions call track() and the debounce coalesces into one POST.
  function useFormSession(formId) {
    const sessionIdRef = useRef(null);
    const startedAtRef = useRef(null);
    const timerRef = useRef(null);
    const attribution = useMemo(() => readAttribution(), []);

    if (sessionIdRef.current === null) sessionIdRef.current = loadSession(formId);
    if (startedAtRef.current === null) startedAtRef.current = Date.now();

    const track = useCallback((body) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      const sessionId = sessionIdRef.current;
      const startedAt = startedAtRef.current;
      timerRef.current = setTimeout(() => {
        const time_on_form = Math.max(0, Math.round((Date.now() - startedAt) / 1000));
        fetch('/api/track.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            session_id: sessionId,
            form_id: formId,
            time_on_form,
            ...attribution,
            ...body,
          }),
        }).catch(() => { /* tracking is best-effort; swallow errors */ });
      }, 600);
    }, [formId, attribution]);

    return {
      sessionId: sessionIdRef.current,
      track,
      attribution,
      startedAt: startedAtRef.current,
    };
  }

  // --- submitLead ------------------------------------------------------------
  // Multipart POST to /api/submit.php. files is an array of File objects.
  // Returns { ok: true, lead_id, email_sent } on success, throws on failure.
  async function submitLead(args) {
    const { sessionId, formId, productInterest, fields, files, attribution, startedAt, lastField, stepReached } = args;
    const fd = new FormData();
    fd.append('session_id', sessionId);
    fd.append('form_id', formId);
    fd.append('product_interest', productInterest || '');
    fd.append('page_url', attribution.page_url);
    fd.append('referrer', attribution.referrer);
    fd.append('utm_source', attribution.utm_source);
    fd.append('utm_medium', attribution.utm_medium);
    fd.append('utm_campaign', attribution.utm_campaign);
    fd.append('utm_content', attribution.utm_content);
    fd.append('utm_term', attribution.utm_term);
    fd.append('last_field', lastField || '');
    fd.append('step_reached', String(stepReached || 0));
    fd.append('time_on_form', String(Math.max(0, Math.round((Date.now() - startedAt) / 1000))));
    fd.append('name', fields.name || '');
    fd.append('email', fields.email || '');
    fd.append('phone', fields.phone || '');
    fd.append('message', fields.message || '');
    fd.append('fields_data', JSON.stringify(fields.fields_data || {}));
    fd.append('fields_touched', JSON.stringify(fields.fields_touched || []));

    (files || []).forEach((f) => fd.append('files[]', f));

    const r = await fetch('/api/submit.php', {
      method: 'POST',
      credentials: 'same-origin',
      body: fd,
    });
    const j = await r.json().catch(() => null);
    if (!r.ok || !j || j.ok !== true) {
      const msg = (j && j.error) || `Submit failed (${r.status})`;
      throw new Error(msg);
    }
    return j;
  }

  // --- File helpers ----------------------------------------------------------
  function fileExtOk(name) {
    const ext = (name.split('.').pop() || '').toLowerCase();
    return FORM_ALLOWED_EXT.includes(ext);
  }
  function formatBytes(n) {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  }
  function validateFile(file, existingCount) {
    if (existingCount >= FORM_MAX_FILES) {
      return `Max ${FORM_MAX_FILES} files.`;
    }
    if (!fileExtOk(file.name)) {
      return `Only JPG, PNG, WebP, HEIC, or PDF.`;
    }
    if (file.size > FORM_MAX_BYTES) {
      return `Each file must be under ${Math.round(FORM_MAX_BYTES / 1024 / 1024)} MB. "${file.name}" is ${formatBytes(file.size)}.`;
    }
    return null;
  }

  // --- FileUploadField -------------------------------------------------------
  // Controlled component. `files` is an array of File; `setFiles` replaces it.
  // Shows a "+ Add files" trigger, accepted chips with × remove, and an inline
  // error line when a file is rejected.
  function FileUploadField({ files, setFiles, error }) {
    const inputRef = useRef(null);
    const [localErr, setLocalErr] = useState('');

    const onPick = (e) => {
      const picked = Array.from(e.target.files || []);
      const next = [...files];
      let firstErr = '';
      for (const f of picked) {
        const err = validateFile(f, next.length);
        if (err) { firstErr = firstErr || err; continue; }
        // Dedupe by name+size (best-effort).
        if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
        next.push(f);
      }
      setFiles(next);
      setLocalErr(firstErr);
      // Reset the input so re-picking the same file fires onChange again.
      e.target.value = '';
    };

    const onRemove = (i) => {
      const next = files.slice();
      next.splice(i, 1);
      setFiles(next);
      setLocalErr('');
    };

    const remaining = FORM_MAX_FILES - files.length;
    const trigger = () => inputRef.current && inputRef.current.click();
    const onKey = (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); trigger(); }
    };

    return (
      <div className="field field-files">
        <label>Photos or floor plans (optional)</label>
        <div className="files-help">
          Up to {FORM_MAX_FILES} files, {Math.round(FORM_MAX_BYTES / 1024 / 1024)} MB each. JPG, PNG, WebP, HEIC, or PDF.
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={FORM_ALLOWED_ACCEPT}
          multiple
          onChange={onPick}
          style={{position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0}}
          aria-hidden="true"
          tabIndex={-1}
        />

        <div className="files-list">
          {files.map((f, i) => (
            <div className="file-chip" key={`${f.name}-${f.size}-${i}`}>
              <span className="file-chip-name" title={f.name}>{f.name}</span>
              <span className="file-chip-size">{formatBytes(f.size)}</span>
              <button
                type="button"
                className="file-chip-remove"
                aria-label={`Remove ${f.name}`}
                onClick={() => onRemove(i)}
              >×</button>
            </div>
          ))}
          {remaining > 0 && (
            <button
              type="button"
              className="file-add"
              onClick={trigger}
              onKeyDown={onKey}
            >
              <span className="file-add-plus" aria-hidden="true">+</span>
              <span>{files.length === 0 ? 'Add files' : `Add another (${remaining} left)`}</span>
            </button>
          )}
        </div>

        {(localErr || error) && <div className="err-msg">{localErr || error}</div>}
      </div>
    );
  }

  // --- CommentUploadsStep ----------------------------------------------------
  // The "anything we should see?" step. Replaces the legacy slot picker.
  // Used by every WizardForm via getCommentUploadsStep().
  function CommentUploadsStep({ data, update, errors }) {
    return (
      <div className="field-stack">
        <div className="field">
          <label>Anything we should know? (optional)</label>
          <textarea
            rows={5}
            placeholder="Site quirks, ideas, photos coming separately, deadlines — anything that helps us prep."
            value={data.message || ''}
            onChange={(e) => update('message', e.target.value)}
          />
        </div>
        <FileUploadField
          files={data.files || []}
          setFiles={(next) => update('files', next)}
          error={errors.files}
        />
      </div>
    );
  }

  // --- Shared step renderers (used by every wizard form) --------------------
  // StepPostal: postal code + property type. Validation: Canadian postal.
  function StepPostal({ data, update, errors }) {
    return (
      <div className="field-stack">
        <div className="field">
          <label>Postal code</label>
          <input
            placeholder="M5V 2T6"
            value={data.postal || ''}
            onChange={(e) => update('postal', e.target.value)}
            className={errors.postal ? 'err' : ''}
          />
          {errors.postal && <div className="err-msg">{errors.postal}</div>}
        </div>
        <div className="field">
          <label>Property type</label>
          <div className="seg">
            {['Residential', 'Commercial'].map((v) => (
              <button
                key={v}
                type="button"
                className={data.venue === v ? 'on' : ''}
                onClick={() => update('venue', v)}
              >{v}</button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // StepWhen: timing dropdown buttons.
  function StepWhen({ data, update, errors }) {
    return (
      <div className="opts">
        {['1–3 months', '3–6 months', '6–12 months', 'Just researching'].map((v) => (
          <button
            key={v}
            type="button"
            className={`opt ${data.when === v ? 'sel' : ''}`}
            onClick={() => update('when', v)}
          >
            <span>{v}</span>
            <span className="dot" />
          </button>
        ))}
        {errors.when && <div className="err-msg">{errors.when}</div>}
      </div>
    );
  }

  // StepContact: name + email + phone + preferred contact channel.
  function StepContact({ data, update, errors }) {
    return (
      <div className="field-stack">
        <div className="field">
          <label>Name</label>
          <input
            placeholder="Jane Tremblay"
            value={data.name || ''}
            onChange={(e) => update('name', e.target.value)}
            className={errors.name ? 'err' : ''}
          />
          {errors.name && <div className="err-msg">{errors.name}</div>}
        </div>
        <div className="field-row">
          <div className="field">
            <label>Email</label>
            <input
              placeholder="jane@email.com"
              value={data.email || ''}
              onChange={(e) => update('email', e.target.value)}
              className={errors.email ? 'err' : ''}
            />
            {errors.email && <div className="err-msg">{errors.email}</div>}
          </div>
          <div className="field">
            <label>Phone</label>
            <input
              placeholder="416 555 0123"
              value={data.phone || ''}
              onChange={(e) => update('phone', e.target.value)}
              className={errors.phone ? 'err' : ''}
            />
            {errors.phone && <div className="err-msg">{errors.phone}</div>}
          </div>
        </div>
        <div className="field">
          <label>Preferred contact</label>
          <div className="seg">
            {['Phone', 'Email', 'Text'].map((v) => (
              <button
                key={v}
                type="button"
                className={data.contactPref === v ? 'on' : ''}
                onClick={() => update('contactPref', v)}
              >{v}</button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Standard validators for the shared steps. Each form composes them into its
  // step list via the validate field; centralising them here keeps the rules
  // identical across the 5 wizard forms.
  function validatePostal(d) {
    return isCanadianPostalCode(d.postal) ? {} : { postal: 'Enter a valid Canadian postal code.' };
  }
  function validateWhen(d) {
    return d.when ? {} : { when: 'Pick a timing.' };
  }
  function validateContact(d) {
    const e = {};
    if (!String(d.name || '').trim()) e.name = 'Your name, please.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email || '')) e.email = 'Valid email, please.';
    if (!/^[\d\s\-+()]{10,}$/.test(d.phone || '')) e.phone = 'Valid phone, please.';
    return e;
  }

  function getCommentUploadsStep(opts) {
    const o = opts || {};
    return {
      id: 'uploads',
      title: o.title || 'Anything we should see?',
      sub:   o.sub   || "Drop a few photos of your space if you have them. Or just hit send — we'll talk it through on the call.",
      render: CommentUploadsStep,
      // No validation: comment + uploads are both optional.
      validate: () => ({}),
    };
  }

  // --- WizardForm ------------------------------------------------------------
  // Drives the form-card UI: progress bar, step transitions, validation, the
  // final "we'll be in touch within one business day" confirmation card.
  //
  // Props
  // - formId         : "main" | "pergolas" | "saunas" | "hottubs" | "swimspas"
  // - sectionId      : DOM id for the wrapping <section> (default "book")
  // - eyebrow        : top tag above the title (e.g. "Pergola consultation")
  // - title          : h2 text (e.g. "Two minutes. No pressure.")
  // - body           : paragraph under the title
  // - initialData    : initial form state
  // - steps          : array of { id, title, sub?, render, validate }
  //                    render({data, update, errors, toggleArr}) returns JSX.
  //                    validate(data) returns {field: msg} or {}.
  // - productInterest: string OR (data)=>string. Used on submit + tracking.
  // - confirmMeta    : (data)=>[{label, value}, ...] for the confirmation card.
  function WizardForm({
    formId,
    sectionId,
    eyebrow,
    title,
    body,
    initialData,
    steps,
    productInterest,
    confirmMeta,
  }) {
    const allSteps = useMemo(() => steps.concat([{ id: 'done', title: "We've got you.", sub: '', isConfirm: true }]), [steps]);
    const [step, setStep] = useState(0);
    const [data, setData] = useState(() => ({
      message: '',
      files: [],
      fields_touched: [],
      ...(initialData || {}),
    }));
    const [errors, setErrors] = useState({});
    const [direction, setDirection] = useState(1);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const session = useFormSession(formId);

    const update = useCallback((k, v) => {
      setData((d) => {
        const touched = d.fields_touched && d.fields_touched.includes(k) ? d.fields_touched : [...(d.fields_touched || []), k];
        const next = { ...d, [k]: v, fields_touched: touched };
        // Track on each user edit, debounced.
        const productInt = typeof productInterest === 'function' ? productInterest(next) : productInterest;
        session.track({
          name: next.name || '',
          email: next.email || '',
          phone: next.phone || '',
          product_interest: productInt || '',
          message: next.message || '',
          fields_data: serializeFields(next),
          fields_touched: next.fields_touched,
          last_field: k,
          step_reached: step,
        });
        return next;
      });
    }, [productInterest, session, step]);

    const toggleArr = useCallback((k, v) => {
      setData((d) => {
        const arr = (d[k] || []).includes(v)
          ? (d[k] || []).filter((x) => x !== v)
          : [...(d[k] || []), v];
        const touched = d.fields_touched && d.fields_touched.includes(k) ? d.fields_touched : [...(d.fields_touched || []), k];
        const next = { ...d, [k]: arr, fields_touched: touched };
        const productInt = typeof productInterest === 'function' ? productInterest(next) : productInterest;
        session.track({
          name: next.name || '',
          email: next.email || '',
          phone: next.phone || '',
          product_interest: productInt || '',
          message: next.message || '',
          fields_data: serializeFields(next),
          fields_touched: next.fields_touched,
          last_field: k,
          step_reached: step,
        });
        return next;
      });
    }, [productInterest, session, step]);

    const validateStep = useCallback((i, d) => {
      const s = steps[i];
      if (!s || !s.validate) return {};
      const e = s.validate(d) || {};
      setErrors(e);
      return e;
    }, [steps]);

    const next = useCallback(() => {
      const e = validateStep(step, data);
      if (Object.keys(e).length > 0) return;
      setDirection(1);
      setStep((s) => Math.min(allSteps.length - 1, s + 1));
    }, [step, data, validateStep, allSteps.length]);

    const back = useCallback(() => {
      setDirection(-1);
      setStep((s) => Math.max(0, s - 1));
    }, []);

    const total = steps.length; // confirm step is index === total
    const onBookable = step < total;
    const isLastInput = step === total - 1;
    const progress = onBookable ? step / total : 1;

    const doSubmit = useCallback(async () => {
      // Validate the final input step before submitting.
      const e = validateStep(step, data);
      if (Object.keys(e).length > 0) return;
      setSubmitting(true);
      setSubmitError('');
      try {
        const productInt = typeof productInterest === 'function' ? productInterest(data) : productInterest;
        await submitLead({
          sessionId: session.sessionId,
          formId,
          productInterest: productInt,
          fields: {
            name: data.name || '',
            email: data.email || '',
            phone: data.phone || '',
            message: data.message || '',
            fields_data: serializeFields(data),
            fields_touched: data.fields_touched || [],
          },
          files: data.files || [],
          attribution: session.attribution,
          startedAt: session.startedAt,
          lastField: 'submit',
          stepReached: step + 1,
        });
        // On success, clear the session so the next visit starts fresh.
        clearSession(formId);
        setSubmitted(true);
        setDirection(1);
        setStep(allSteps.length - 1);
      } catch (err) {
        setSubmitError(err && err.message ? err.message : 'Could not submit. Please try again or call us.');
      } finally {
        setSubmitting(false);
      }
    }, [data, step, validateStep, session, formId, productInterest, allSteps.length]);

    const current = allSteps[step];
    const isConfirm = current && current.isConfirm;
    const StepRender = !isConfirm && steps[step] && steps[step].render;

    return (
      <section className="form-section" id={sectionId || 'book'}>
        <div className="wrap">
          <div className="form-head">
            <span className="eyebrow">{eyebrow}</span>
            <h2 className="h2">{title}</h2>
            {body && <p className="body-lg">{body}</p>}
          </div>

          <div className="form-card">
            <div className="form-progress">
              <div className="form-progress-bar" style={{ width: `${progress * 100}%` }} />
              <div className="form-progress-meta">
                <span>{onBookable ? `Step ${step + 1} of ${total}` : 'Sent'}</span>
                {step > 0 && onBookable && (
                  <button type="button" className="back-btn" onClick={back}>← Back</button>
                )}
              </div>
            </div>

            <div className="form-stage">
              <div key={step} className={`step ${direction > 0 ? 'fwd' : 'back'}`}>
                <h3 className="step-title">{current ? current.title : ''}</h3>
                {current && current.sub && <p className="step-sub">{current.sub}</p>}

                {!isConfirm && StepRender && (
                  <StepRender
                    data={data}
                    update={update}
                    toggleArr={toggleArr}
                    errors={errors}
                  />
                )}

                {isConfirm && submitted && (
                  <ConfirmCard data={data} confirmMeta={confirmMeta} />
                )}

                {submitError && (
                  <div className="err-msg" style={{ marginTop: 12 }}>{submitError}</div>
                )}
              </div>
            </div>

            {onBookable && (
              <div className="form-foot">
                <div className="form-foot-meta">
                  {/* Per-step summary chip — left for the page to fill if needed */}
                </div>
                {isLastInput ? (
                  <button
                    type="button"
                    className="btn dark"
                    onClick={doSubmit}
                    disabled={submitting}
                  >
                    {submitting ? 'Sending…' : 'Send'}
                    <span className="arrow">→</span>
                  </button>
                ) : (
                  <button type="button" className="btn dark" onClick={next}>
                    Continue
                    <span className="arrow">→</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  function ConfirmCard({ data, confirmMeta }) {
    const meta = (typeof confirmMeta === 'function') ? (confirmMeta(data) || []) : [];
    return (
      <div className="confirm">
        <div className="check-circle">✓</div>
        <h3 className="confirm-title">We'll be in touch within one business day.</h3>
        <p className="confirm-body">
          A confirmation is on its way to <b>{data.email || 'your inbox'}</b>.
          Bring photos of your space if you didn't already attach any — it makes the call ten times more useful.
        </p>
        {meta.length > 0 && (
          <div className="confirm-meta">
            {meta.map((m, i) => (
              <div key={i}><span>{m.label}</span><b>{m.value}</b></div>
            ))}
          </div>
        )}
        <a className="btn dark" href="tel:4167088053">Call us now · 416 708 8053 <span className="arrow">↗</span></a>
      </div>
    );
  }

  // Strip File objects out of fields_data so JSON.stringify doesn't choke.
  function serializeFields(d) {
    const out = {};
    for (const k of Object.keys(d)) {
      const v = d[k];
      if (k === 'files') {
        out.files = (v || []).map((f) => ({ name: f.name, size: f.size, type: f.type }));
      } else if (k === 'fields_touched') {
        // Skip — sent separately.
        continue;
      } else if (v instanceof File) {
        continue;
      } else {
        out[k] = v;
      }
    }
    return out;
  }

  // --- Postal-code helper, used by several step.validate() functions ----------
  function isCanadianPostalCode(s) {
    const pc = String(s || '').trim().toUpperCase().replace(/\s/g, '');
    return /^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(pc);
  }

  // --- Expose to global ------------------------------------------------------
  const exports = {
    WizardForm,
    FileUploadField,
    CommentUploadsStep,
    getCommentUploadsStep,
    StepPostal,
    StepWhen,
    StepContact,
    validatePostal,
    validateWhen,
    validateContact,
    submitLead,
    useFormSession,
    isCanadianPostalCode,
    formatBytes,
    FORM_MAX_BYTES,
    FORM_MAX_FILES,
    FORM_ALLOWED_EXT,
  };
  // Expose to both window (browser) and globalThis (Node vm sandbox during
  // prerender). In the browser, window === globalThis so the second assign
  // is a noop but harmless.
  Object.assign(window, exports);
  if (typeof globalThis !== 'undefined') Object.assign(globalThis, exports);
})();


/* === contact-page.jsx === */
// Solara Living - Contact page

const { useState, useEffect, useRef } = React;

function Nav({ onDark }) {
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);
  const close = () => setMenuOpen(false);
  return (
    <>
      <div className={`nav ${onDark ? "on-dark" : ""}`}>
        <div className="nav-pill">
          <a href="Solara Living.html" className="nav-brand">
            <img className="mark" src="images/solara-logo.svg" alt="" aria-hidden="true" />
            <span>SOLARA <b>LIVING</b></span>
          </a>
          <a href="Solara Living.html" className="nav-link">Home</a>
          <a href="about.html" className="nav-link">About</a>
          <div className="nav-dropdown nav-link" style={{padding:0}}>
            <a href="#" className="nav-link" style={{display:"inline-flex", alignItems:"center", gap:6}}>
              Products
              <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </a>
            <div className="nav-dropdown-menu">
              <a href="pergolas.html">Pergolas</a>
              <a href="saunas.html">Saunas</a>
              <a href="hot-tubs.html">Hot tubs</a>
              <a href="swim-spas.html">Swim spas</a>
            </div>
          </div>
          <a href="contact.html" className="nav-link active">Contact</a>
          <button type="button" className="nav-hamburger" aria-label={menuOpen ? "Close menu" : "Open menu"} aria-expanded={menuOpen} onClick={() => setMenuOpen(o => !o)}>
            {menuOpen ? (
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            )}
          </button>
          <a href="#book" className="nav-cta" onClick={(e)=>{e.preventDefault(); document.getElementById('book')?.scrollIntoView({behavior:'smooth',block:'start'});}}>Request a quote</a>
        </div>
      </div>
      <div className={`nav-drawer-backdrop ${menuOpen ? "open" : ""}`} onClick={close} aria-hidden="true" />
      <div className={`nav-drawer ${menuOpen ? "open" : ""}`} role="dialog" aria-modal="true" aria-label="Site menu">
        <div className="nav-drawer-section">Navigate</div>
        <a href="Solara Living.html" className="nav-drawer-link" onClick={close}>Home <span className="arrow">→</span></a>
        <a href="about.html" className="nav-drawer-link" onClick={close}>About <span className="arrow">→</span></a>
        <a href="contact.html" className="nav-drawer-link active" onClick={close}>Contact <span className="arrow">→</span></a>
        <div className="nav-drawer-section">Products</div>
        <a href="pergolas.html" className="nav-drawer-link" onClick={close}>Pergolas <span className="arrow">→</span></a>
        <a href="saunas.html" className="nav-drawer-link" onClick={close}>Saunas <span className="arrow">→</span></a>
        <a href="hot-tubs.html" className="nav-drawer-link" onClick={close}>Hot tubs <span className="arrow">→</span></a>
        <a href="swim-spas.html" className="nav-drawer-link" onClick={close}>Swim spas <span className="arrow">→</span></a>
      </div>
    </>
  );
}

function useReveal(){
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("in"); });
    }, { threshold: 0.18 });
    el.querySelectorAll('[data-reveal]').forEach(n => io.observe(n));
    return () => io.disconnect();
  }, []);
  return ref;
}

// ---- Hero (compact) ----
function Hero(){
  const headingRaw = _C('contact.hero.heading', "Let's talk\nabout your space.");
  const headingHtml = String(headingRaw).replace(/\n/g, '<br/>');
  const sub = _C('contact.hero.subheading', "Tell us what you have in mind. We'll book a 30-minute consultation, walk your site, and send a fixed quote within 48 hours.");
  const heroSrc = _C('contact.hero.src', 'images/gallery/pergola-mountain-view.jpg');
  const heroAlt = _C('contact.hero.alt', 'A Solara Living install with a sweeping view');
  const primaryBtn = _C('contact.hero.primaryButtonText', 'Request a quote');
  const secondaryBtn = _C('contact.hero.secondaryButtonText', 'Just have a question?');
  const breadcrumb = _C('contact.breadcrumbLeaf', 'Contact');
  return (
    <section className="p-hero c-hero">
      <div className="p-hero-photo">
        <img src={heroSrc} alt={heroAlt} />
      </div>
      <div className="p-hero-veil" />
      <div className="p-breadcrumb wrap">
        <a href="Solara Living.html"><span className="arrow">←</span> Solara Living</a> &nbsp;·&nbsp; {breadcrumb}
      </div>
      <div className="wrap p-hero-inner">
        <div className="p-hero-copy">
          <h1 className="p-hero-headline" dangerouslySetInnerHTML={{__html: headingHtml}} />
          <div className="p-hero-sub-block">
            <p className="p-hero-sub">{sub}</p>
            <div className="p-hero-actions">
              <a className="btn" href="#book" onClick={()=>{ window.location.hash = "quote"; }}>{primaryBtn} <span className="arrow">↗</span></a>
              <a className="btn ghost" href="#book" onClick={()=>{ window.location.hash = "message"; }}>{secondaryBtn} <span className="arrow">→</span></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---- Quick info cards ----
function InfoCards(){
  const ref = useReveal();
  const fallback = [
    {label: "Call",     value: "416 708 8053",                meta: "Mon – Sat · 9am – 6pm",        href: "tel:4167088053"},
    {label: "Email",    value: "hello@\nsolaraliving.ca",     meta: "We reply within one business day.", href: "mailto:hello@solaraliving.ca"},
    {label: "Visit",    value: "7250 Keele St.\nUnit 255",    meta: "Vaughan ON · L4K 1Z8",         href: "https://www.google.com/maps/search/?api=1&query=7250+Keele+St+Unit+255+Vaughan+ON+L4K+1Z8"},
    {label: "Showroom", value: "By appointment\nonly",        meta: "Book a slot, bring photos of your space.", href: ""},
  ];
  const cards = _CA('contact.infoCards', fallback);
  return (
    <section className="c-info" ref={ref}>
      <div className="wrap c-info-grid">
        {cards.map((c, i) => {
          const valueHtml = String(c.value || '').replace(/\n/g, '<br/>');
          const isLink = c.href && c.href.length > 0;
          const isExternal = c.href && /^https?:\/\//.test(c.href);
          const inner = (
            <>
              <div className="c-info-label">{c.label}</div>
              <div className="c-info-value" dangerouslySetInnerHTML={{__html: valueHtml}} />
              {c.meta && <div className="c-info-meta">{c.meta}</div>}
            </>
          );
          return isLink ? (
            <a key={c._key || c.label || i} className="c-info-card" data-reveal href={c.href}
               {...(isExternal ? {target:"_blank", rel:"noreferrer"} : {})}>{inner}</a>
          ) : (
            <div key={c._key || c.label || i} className="c-info-card no-link" data-reveal>{inner}</div>
          );
        })}
      </div>
    </section>
  );
}

// ---- Lead form (mode toggle: quote vs. message) ----
// Wires through the shared helpers in solara-form.jsx:
//   - useFormSession(formId)  → session_id + debounced /api/track.php pings
//   - submitLead(...)         → multipart POST to /api/submit.php
//   - FileUploadField         → 8 MB × 3 file picker (Quote mode only)
function ContactForm(){
  const [mode, setMode] = useState("quote"); // "quote" | "message"
  const [data, setData] = useState({
    considering: [],
    name: "", email: "", phone: "",
    postal: "",
    venue: "Residential",
    when: "1–3 months",
    message: "",
    contactPref: "Phone",
    files: [],
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // The contact form uses its own session id so a lead started here doesn't
  // collide with one from the home or product pages.
  const session = useFormSession("contact");

  // Deep-link to message mode via #message hash
  useEffect(() => {
    const apply = () => {
      const h = window.location.hash.replace("#", "");
      if (h === "message") setMode("message");
      else if (h === "quote" || h === "book") setMode("quote");
    };
    apply();
    window.addEventListener("hashchange", apply);
    return () => window.removeEventListener("hashchange", apply);
  }, []);

  // Pull product_interest off the considering[] list (matches the home form's
  // derivation): single product → that product, multi → "multiple".
  const deriveContactProduct = (d) => {
    const c = d.considering || [];
    if (c.length === 0) return "";
    if (c.length > 1) return "multiple";
    const map = { "Pergola": "pergola", "Sauna": "sauna", "Hot tub": "hottub", "Swim spa": "swimspa" };
    return map[c[0]] || "multiple";
  };

  const update = (k, v) => setData(d => {
    const next = { ...d, [k]: v };
    session.track({
      name: next.name, email: next.email, phone: next.phone,
      message: next.message, product_interest: deriveContactProduct(next),
      fields_data: { ...next, files: (next.files || []).map(f => ({name: f.name, size: f.size})) },
      fields_touched: [k],
      last_field: k,
      step_reached: mode === "quote" ? 1 : 2,
    });
    return next;
  });
  const toggleConsidering = (v) => {
    setData(d => {
      const arr = d.considering.includes(v) ? d.considering.filter(x => x !== v) : [...d.considering, v];
      return {...d, considering: arr};
    });
  };

  const switchMode = (next) => {
    setMode(next);
    setErrors({});
    setSubmitError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (!data.name.trim()) er.name = "Your name, please.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) er.email = "Valid email, please.";
    if (mode === "quote") {
      if (data.considering.length === 0) er.considering = "Pick at least one.";
      if (!/^[\d\s\-+()]{10,}$/.test(data.phone)) er.phone = "Valid phone, please.";
    } else {
      // message mode: phone optional, but if provided must look valid
      if (data.phone.trim() && !/^[\d\s\-+()]{10,}$/.test(data.phone)) er.phone = "That phone number doesn't look right.";
      if (!data.message.trim()) er.message = "Add a quick note so we know how to help.";
    }
    setErrors(er);
    if (Object.keys(er).length > 0) return;

    setSubmitting(true);
    setSubmitError("");
    try {
      const productInterest = mode === "quote" ? (deriveContactProduct(data) || "multiple") : "multiple";
      await submitLead({
        sessionId: session.sessionId,
        formId: "contact",
        productInterest,
        fields: {
          name: data.name,
          email: data.email,
          phone: data.phone,
          message: data.message,
          fields_data: {
            mode,
            considering: data.considering,
            postal: data.postal,
            venue: data.venue,
            when: data.when,
            contactPref: data.contactPref,
            files: (data.files || []).map(f => ({name: f.name, size: f.size})),
          },
          fields_touched: ["mode"],
        },
        files: mode === "quote" ? (data.files || []) : [],
        attribution: session.attribution,
        startedAt: session.startedAt,
        lastField: mode,
        stepReached: mode === "quote" ? 2 : 1,
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError((err && err.message) || "Could not send. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="c-form-section" id="book">
        <div className="wrap">
          <div className="c-form-card confirm">
            <div className="check-circle">✓</div>
            <h2 className="confirm-title">
              {mode === "quote" ? "Got it. We'll be in touch." : "Thanks, message received."}
            </h2>
            <p className="confirm-body">
              {mode === "quote" ? (
                <>A note is on its way to <b>{data.email}</b>. Someone from the Solara team will reach out within one business day to confirm a 30-minute consultation. If it's urgent, you can also call us at <b>416 708 8053</b>.</>
              ) : (
                <>We'll reply to <b>{data.email}</b> within one business day. No quote, no callback, no follow-up sequence, just an answer to your question. If it's urgent, give us a ring at <b>416 708 8053</b>.</>
              )}
            </p>
            <div className="confirm-meta">
              {mode === "quote" ? (
                <>
                  <div><span>Interested in</span><b>{data.considering.join(", ")}</b></div>
                  <div><span>Property</span><b>{data.venue}</b></div>
                  <div><span>Timing</span><b>{data.when}</b></div>
                  <div><span>Contact</span><b>{data.contactPref}</b></div>
                </>
              ) : (
                <>
                  <div><span>From</span><b>{data.name}</b></div>
                  <div><span>Email</span><b>{data.email}</b></div>
                  {data.phone.trim() && <div><span>Phone</span><b>{data.phone}</b></div>}
                  <div><span>Reply by</span><b>Email · 1 business day</b></div>
                </>
              )}
            </div>
            <div style={{display:"flex", gap:"12px", flexWrap:"wrap"}}>
              <a className="btn dark" href="tel:4167088053">Call us · 416 708 8053 <span className="arrow">↗</span></a>
              <a className="btn ghost-dark" href="Solara Living.html">Back to home <span className="arrow">→</span></a>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const considering = [
    {v:"Pergola",  src:"images/gallery/pergola-twilight.jpg"},
    {v:"Sauna",    src:"images/gallery/sauna-cabin-forest.jpg"},
    {v:"Hot tub",  src:"images/gallery/hot-tub-night-bokeh.jpg"},
    {v:"Swim spa", src:"images/gallery/swim-spa-courtyard.jpg"},
  ];

  const isQuote = mode === "quote";

  return (
    <section className="c-form-section" id="book">
      <div className="wrap">
        <div className="c-form-head">
          <span className="eyebrow">{isQuote ? "Request a quote" : "Send a message"}</span>
          <h2 className="h2">
            {isQuote ? (<>A few details, and we'll<br/>send you a quote.</>) : (<>A quick question?<br/>Drop us a line.</>)}
          </h2>
          <p className="body-lg">
            {isQuote
              ? "Tell us about your project and we'll follow up within one business day to confirm a 30-minute consultation. Fixed quote within 48 hours of your site visit."
              : "Not ready for a quote, just have a question? Send us a note and we'll reply by email within one business day. No follow-up sequence, no sales calls."}
          </p>
        </div>

        {/* Mode tabs */}
        <div className="c-mode-tabs" role="tablist" aria-label="Contact reason">
          <button
            type="button" role="tab" aria-selected={isQuote}
            className={`c-mode-tab ${isQuote ? "on" : ""}`}
            onClick={() => switchMode("quote")}
          >
            <span className="c-mode-tab-body">
              <b>Request a quote</b>
              <span>Site visit · fixed quote in 48 hours</span>
            </span>
          </button>
          <button
            type="button" role="tab" aria-selected={!isQuote}
            className={`c-mode-tab ${!isQuote ? "on" : ""}`}
            onClick={() => switchMode("message")}
          >
            <span className="c-mode-tab-body">
              <b>Send a message</b>
              <span>Just a question · reply by email</span>
            </span>
          </button>
        </div>

        <form className="c-form-card" onSubmit={submit} noValidate>
          {/* Considering picker, quote only */}
          {isQuote && (
            <div className="c-field-group">
              <label className="c-group-label">I'm interested in</label>
              <div className="c-considering">
                {considering.map(o => {
                  const sel = data.considering.includes(o.v);
                  return (
                    <button
                      key={o.v}
                      type="button"
                      className={`c-considering-card ${sel ? "sel" : ""}`}
                      onClick={() => toggleConsidering(o.v)}
                      aria-pressed={sel}
                    >
                      <img src={o.src} alt="" />
                      <div className="c-considering-meta">
                        <span>{o.v}</span>
                        <span className="check">{sel ? "✓" : "+"}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
              {errors.considering && <div className="err-msg">{errors.considering}</div>}
            </div>
          )}

          <div className="c-field-row two">
            <div className="field">
              <label>Name</label>
              <input value={data.name} onChange={e => update("name", e.target.value)} placeholder="Jane Tremblay" className={errors.name ? "err" : ""} />
              {errors.name && <div className="err-msg">{errors.name}</div>}
            </div>
            <div className="field">
              <label>Email</label>
              <input value={data.email} onChange={e => update("email", e.target.value)} placeholder="jane@email.com" className={errors.email ? "err" : ""} />
              {errors.email && <div className="err-msg">{errors.email}</div>}
            </div>
          </div>

          {isQuote ? (
            <>
              <div className="c-field-row two">
                <div className="field">
                  <label>Phone</label>
                  <input value={data.phone} onChange={e => update("phone", e.target.value)} placeholder="416 555 0123" className={errors.phone ? "err" : ""} />
                  {errors.phone && <div className="err-msg">{errors.phone}</div>}
                </div>
                <div className="field">
                  <label>Postal code</label>
                  <input value={data.postal} onChange={e => update("postal", e.target.value)} placeholder="L4K 1Z8" />
                </div>
              </div>

              <div className="c-field-row two">
                <div className="field">
                  <label>Property type</label>
                  <div className="seg">
                    {["Residential", "Commercial"].map(v => (
                      <button key={v} type="button" className={data.venue === v ? "on" : ""} onClick={() => update("venue", v)}>{v}</button>
                    ))}
                  </div>
                </div>
                <div className="field">
                  <label>Timing</label>
                  <div className="seg seg-wrap">
                    {["1–3 months","3–6 months","6–12 months","Researching"].map(v => (
                      <button key={v} type="button" className={data.when === v ? "on" : ""} onClick={() => update("when", v)}>{v}</button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="field">
                <label>Tell us about your space (optional)</label>
                <textarea
                  value={data.message}
                  onChange={e => update("message", e.target.value)}
                  placeholder="Backyard size, what you're picturing, anything else useful…"
                  rows={4}
                />
              </div>

              <FileUploadField
                files={data.files || []}
                setFiles={(next) => update("files", next)}
                error={errors.files}
              />

              <div className="field">
                <label>Preferred contact</label>
                <div className="seg">
                  {["Phone","Email","Text"].map(v => (
                    <button key={v} type="button" className={data.contactPref === v ? "on" : ""} onClick={() => update("contactPref", v)}>{v}</button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="field">
                <label>Phone <span className="opt">(optional)</span></label>
                <input value={data.phone} onChange={e => update("phone", e.target.value)} placeholder="416 555 0123" className={errors.phone ? "err" : ""} />
                {errors.phone && <div className="err-msg">{errors.phone}</div>}
              </div>

              <div className="field">
                <label>Your message</label>
                <textarea
                  value={data.message}
                  onChange={e => update("message", e.target.value)}
                  placeholder="Ask us anything: lead times, materials, what's possible on a tight balcony, whether we deliver to your town, etc."
                  rows={5}
                  className={errors.message ? "err" : ""}
                />
                {errors.message && <div className="err-msg">{errors.message}</div>}
              </div>
            </>
          )}

          {submitError && (
            <div className="err-msg" style={{marginBottom: 12}}>{submitError}</div>
          )}

          <div className="c-form-foot">
            <div className="c-form-meta">
              We never share or sell your details.
            </div>
            <button type="submit" className="btn dark" disabled={submitting}>
              {submitting ? "Sending…" : (isQuote ? "Request my quote" : "Send message")} <span className="arrow">→</span>
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

// ---- Visit (map + address card) ----
function Visit(){
  const ref = useReveal();
  const bbox = _C('contact.visitMapBbox', '-79.5160,43.7920,-79.5010,43.8015');
  const marker = _C('contact.visitMapMarker', '43.7967,-79.5085');
  const osmSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${marker}`;
  const eyebrow = _C('contact.visitEyebrow', 'Visit the studio');
  const headingRaw = _C('contact.visitHeading', 'In Vaughan,\nby appointment.');
  const headingHtml = String(headingRaw).replace(/\n/g, '<br/>');
  const body = _C('contact.visitBody', 'Our studio is a private workspace, not a retail floor. Book a slot so we can give you our full attention, walk you through samples, and review your space together.');
  const addressRaw = _C('contact.visitAddress', '7250 Keele St. Unit 255\nVaughan, Ontario\nL4K 1Z8');
  const addressHtml = String(addressRaw).replace(/\n/g, '<br/>');
  const hours = _C('contact.visitHours', 'By appointment only');
  const hoursMeta = _C('contact.visitHoursMeta', 'Evenings + Saturdays available on request.');
  const mapsLink = _C('contact.visitMapsLink', 'https://www.google.com/maps/search/?api=1&query=7250+Keele+St+Unit+255+Vaughan+ON+L4K+1Z8');
  const phone = _C('site.phone', '416 708 8053');
  const email = _C('site.salesEmail', 'hello@solaraliving.ca');
  return (
    <section className="c-visit" id="visit" ref={ref}>
      <div className="wrap">
        <div className="p-section-head" data-reveal>
          <span className="eyebrow">{eyebrow}</span>
          <h2 className="h2" dangerouslySetInnerHTML={{__html: headingHtml}} />
          <p className="body-lg">{body}</p>
        </div>
        <div className="c-visit-grid" data-reveal>
          <div className="c-map">
            <iframe
              title="Solara Living · 7250 Keele St., Vaughan"
              src={osmSrc}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <a className="c-map-pin" href={mapsLink} target="_blank" rel="noreferrer">
              <span className="c-map-pin-dot" />
              <span>Open in Maps <span className="arrow">↗</span></span>
            </a>
          </div>
          <div className="c-visit-card">
            <div className="c-visit-row">
              <div className="c-visit-label">Address</div>
              <div className="c-visit-value" dangerouslySetInnerHTML={{__html: addressHtml}} />
            </div>
            <div className="c-visit-row">
              <div className="c-visit-label">Hours</div>
              <div className="c-visit-value">{hours}</div>
              {hoursMeta && <div className="c-visit-meta">{hoursMeta}</div>}
            </div>
            <div className="c-visit-row">
              <div className="c-visit-label">Phone</div>
              <div className="c-visit-value"><a href={`tel:${String(phone).replace(/\D/g, '')}`}>{phone}</a></div>
            </div>
            <div className="c-visit-row">
              <div className="c-visit-label">Email</div>
              <div className="c-visit-value"><a href={`mailto:${email}`}>{email}</a></div>
            </div>
            <a className="btn dark c-visit-cta" href="#book" onClick={(e)=>{e.preventDefault(); document.getElementById('book')?.scrollIntoView({behavior:'smooth',block:'start'});}}>
              Book a consultation <span className="arrow">↗</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---- What to expect ----
function Expect(){
  const ref = useReveal();
  const fallback = [
    { num:"01", heading:"Tell us about your space",  body:"Drop us a message or call. Share photos, rough dimensions, and what you're picturing. Sketches on napkins are welcome." },
    { num:"02", heading:"30-minute consultation",     body:"Phone, video, or in-person at the Vaughan studio. We talk options, timelines, and answer every question on your list." },
    { num:"03", heading:"Fixed quote in 48 hours",    body:"Once we've seen the space, we send a fixed, no-surprises quote, with options, lead times, and a clear next step." },
  ];
  const items = _CA('contact.expect', fallback);
  const eyebrow = _C('contact.expectEyebrow', 'What to expect');
  const headingRaw = _C('contact.expectHeading', 'No pressure.\nJust a plan.');
  const headingHtml = String(headingRaw).replace(/\n/g, '<br/>');
  return (
    <section className="c-expect" ref={ref}>
      <div className="wrap">
        <div className="p-section-head" data-reveal>
          <span className="eyebrow">{eyebrow}</span>
          <h2 className="h2" dangerouslySetInnerHTML={{__html: headingHtml}} />
        </div>
        <div className="c-expect-grid">
          {items.map((it,i) => (
            <div className="c-expect-item" key={it._key || i} data-reveal>
              <h3>{it.heading || it.h}</h3>
              <p>{it.body || it.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---- Footer ----
function Footer(){
  return (
    <footer className="footer">
      <div className="wrap footer-inner">
        <div className="footer-brand">
          <div className="wordmark">
            <img className="mark" src="images/solara-logo.svg" alt="" aria-hidden="true" />
            <span>SOLARA <b>LIVING</b></span>
          </div>
          <div className="small">{_C('site.footerCopy', 'Premium outdoor living. Designed, delivered, and installed across the GTA and southern Ontario.')}</div>
        </div>
        <div className="footer-cols">
          <div>
            <div className="col-h">Products</div>
            <a href="pergolas.html">Pergolas</a>
            <a href="saunas.html">Saunas</a>
            <a href="hot-tubs.html">Hot tubs</a>
            <a href="swim-spas.html">Swim spas</a>
          </div>
          <div>
            <div className="col-h">Company</div>
            <a href="about.html">About</a>
            <a href="contact.html">Contact</a>
            <a href="Solara Living.html#gallery">Projects</a>
          </div>
          <div>
            <div className="col-h">Get in touch</div>
            <a href={`tel:${String(_C('site.phone', '416 708 8053')).replace(/\D/g, '')}`}>{_C('site.phone', '416 708 8053')}</a>
            <a href={`mailto:${_C('site.salesEmail', 'hello@solaraliving.ca')}`}>{_C('site.salesEmail', 'hello@solaraliving.ca')}</a>
            <a href="#book">Book a consultation</a>
          </div>
        </div>
      </div>
      <div className="wrap footer-base">
        <span>© {new Date().getFullYear()} Solara Living Inc.</span>
        <span>7250 Keele St. Unit 255 · Vaughan ON L4K 1Z8</span>
      </div>
    </footer>
  );
}

// ---- App ----
function App(){
  const [navDark, setNavDark] = useState(true);
  useEffect(() => {
    const onScroll = () => setNavDark(window.scrollY < window.innerHeight * 0.55);
    onScroll();
    window.addEventListener("scroll", onScroll, {passive:true});
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <>
      <Nav onDark={navDark} />
      <Hero />
      <InfoCards />
      <ContactForm />
      <Visit />
      <Expect />
      <Footer />
    </>
  );
}

ReactDOM.createRoot(document.getElementById("root")).render(<App />);
